import { Handle, Position } from "@xyflow/react"
import type { NodeProps } from "@xyflow/react"

export type FunnelNodeItem = {
  label: string
  value: string
  currentRaw?: number
  prevRaw?: number
  delta?: string        // 増減テキスト（括弧なし。例: "+456"）
  deltaPositive?: boolean
  diffOnly?: boolean    // true: 増減テキストのみ表示（矢印・色なし）
}

export type FunnelNodeData = {
  label: string
  icon: string
  items: FunnelNodeItem[]
  variant?: "main" | "child"
  isLeaf?: boolean
}

const FunnelNode = ({ data }: NodeProps) => {
  const nodeData = data as FunnelNodeData
  const isChild = nodeData.variant === "child"
  const isLeaf = nodeData.isLeaf ?? false

  return (
    <div className={`funnel-node${isChild ? " funnel-node--child" : ""}`}>
      <Handle type="target" position={Position.Left} id="left" />
      {isChild && <Handle type="target" position={Position.Top} id="top" />}
      {!isLeaf && <Handle type="source" position={Position.Right} id="right" />}
      {!isChild && !isLeaf && <Handle type="source" position={Position.Bottom} id="bottom" />}
      <div className="funnel-node__header">
        <i className={`bi ${nodeData.icon}`} />
        <span className="funnel-node__label">{nodeData.label}</span>
      </div>
      <div className="funnel-node__items">
        {nodeData.items.map((item, i) => {
          const hasTrend = item.currentRaw != null && item.prevRaw != null
          const hasDelta = item.delta != null

          const isUp = hasTrend
            ? item.currentRaw! > item.prevRaw!
            : hasDelta ? (item.deltaPositive ?? false) : false
          const isDown = hasTrend
            ? item.currentRaw! < item.prevRaw!
            : hasDelta ? !(item.deltaPositive ?? true) : false

          const diffOnly = item.diffOnly ?? false
          const showArrow = !diffOnly && (isUp || isDown)
          const trendDir = isUp ? "up" : "down"
          const modifier = !diffOnly && (isUp ? " funnel-node__item--up" : isDown ? " funnel-node__item--down" : "")

          let diffText: string | null = null
          if (hasTrend && (isUp || isDown)) {
            const diff = item.currentRaw! - item.prevRaw!
            diffText = `(${diff >= 0 ? "+" : ""}${diff.toLocaleString("ja-JP")})`
          } else if (hasDelta && (isUp || isDown)) {
            diffText = `(${item.delta})`
          }

          return (
            <div key={i} className={`funnel-node__item${modifier}`}>
              <span className="funnel-node__item-label">{item.label}</span>
              <span className="funnel-node__item-value">
                {item.value}
                {showArrow && (
                  <span className={`funnel-node__trend funnel-node__trend--${trendDir}`}>
                    <i className={`bi bi-arrow-${trendDir}-short`} />
                  </span>
                )}
              </span>
              {diffText && (
                <span className={`funnel-node__item-diff${!diffOnly ? ` funnel-node__item-diff--${trendDir}` : ""}`}>
                  {diffText}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default FunnelNode
